import { useState } from "react";
import { Eye, EyeSlash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const Status = ({ info }) => {
  if (!info?.set) return <span className="text-amber-300">Not set</span>;
  if (info.source === "env") return <span className="text-sky-300">Set by environment</span>;
  return (
    <span className="text-emerald-300">
      Saved{info.hint ? ` · ${info.hint}` : ""}
    </span>
  );
};

// Write-only credential fields: values are never read back, only set/source/last-4 hints.
// Empty string on save clears a saved value; keys left untouched are not sent.
export const SecretsForm = ({ keys, secrets, onSave, busy, testid }) => {
  const [draft, setDraft] = useState({});
  const [shown, setShown] = useState({});
  const dirty = Object.keys(draft).length > 0;

  const submit = async () => {
    const ok = await onSave(draft);
    if (ok) setDraft({});
  };

  return (
    <div className="space-y-2" data-testid={testid}>
      {keys.map((k) => {
        const info = secrets?.[k] || {};
        const locked = info.source === "env";
        const clearing = draft[k] === "";
        return (
          <div key={k} className="grid gap-1 sm:grid-cols-[minmax(0,200px)_1fr] sm:items-center">
            <div className="min-w-0">
              <div className="truncate text-xs font-medium text-zinc-300">{info.label || k}</div>
              <div className="truncate font-mono-data text-[10px] text-zinc-500">
                {k} · <Status info={info} />
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <input
                type={shown[k] ? "text" : "password"}
                autoComplete="off"
                disabled={locked || busy}
                value={draft[k] ?? ""}
                placeholder={
                  locked
                    ? "Managed by the backend environment"
                    : clearing
                      ? "Will be cleared on save"
                      : info.set
                        ? "Paste a new value to replace"
                        : "Paste value"
                }
                onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))}
                data-testid={`secret-${k}`}
                className="h-9 min-w-0 flex-1 rounded-lg border border-zinc-800 bg-zinc-950 px-3 text-sm text-zinc-100 outline-none placeholder:text-zinc-600 focus:border-indigo-400/50 disabled:opacity-50"
              />
              <button
                type="button"
                aria-label={shown[k] ? "Hide value" : "Show value"}
                data-testid={`secret-${k}-toggle`}
                onClick={() => setShown((s) => ({ ...s, [k]: !s[k] }))}
                className="rounded-md border border-zinc-800 p-2 text-zinc-400 hover:bg-zinc-800"
              >
                {shown[k] ? <EyeSlash size={13} weight="bold" /> : <Eye size={13} weight="bold" />}
              </button>
              {info.set && info.source === "app" && !clearing && (
                <button
                  type="button"
                  data-testid={`secret-${k}-clear`}
                  onClick={() => setDraft((d) => ({ ...d, [k]: "" }))}
                  className="h-9 rounded-md border border-zinc-800 px-2 text-[11px] text-zinc-400 hover:bg-zinc-800"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        );
      })}
      <div className="flex items-center gap-3 pt-1">
        <Button
          size="sm"
          data-testid={`${testid}-save`}
          disabled={!dirty || busy}
          onClick={submit}
          className="bg-emerald-600 text-white hover:bg-emerald-500"
        >
          Save secrets
        </Button>
        {dirty && (
          <button
            type="button"
            data-testid={`${testid}-discard`}
            onClick={() => setDraft({})}
            className="text-xs text-zinc-500 hover:text-zinc-300"
          >
            Discard
          </button>
        )}
      </div>
    </div>
  );
};

export default SecretsForm;
